import { useState, useEffect } from "react";
import { useAuthenticatedApi } from "./useAuthenticatedApi";
import { showErrorToast, getErrorMessage } from "@/lib/toast-utils";

export interface VINUnit {
  vin: string;
  serialNumber: string;
  secondaryVin: string;
  make: string;
  modelLine: string;
  model: string;
  modelCode: string;
  modelYear: string;
  unitType: string;
  brandSegment: string;
  warrantyStatus: string;
  retailSaleDate: string;
  unitRegistrationDate: string;
  warrantyExpirationDate: string;
  warrantyExpirationMileage: string;
  unitStatus: string;
  offlineDate: string;
  floorplan?: string;
  warrantyExpireDate?: string;
  warrantyExpired?: boolean;
  mockCode?: string;
  mappedFields: Record<string, boolean>;
}

export interface VINsResponse {
  vins: VINUnit[];
  totalCount: number;
  pageSize: number;
  currentPage: number;
}

const has = (val: any) => val !== undefined && val !== null && val !== '' && val !== '-1';

function mapVINFromApi(raw: any): VINUnit {
  const mappedFields: Record<string, boolean> = {
    vin: has(raw.vin),
    serialNumber: has(raw.serialNumber),
    secondaryVin: has(raw.secondaryVin || raw.secondaryVIN),
    make: has(raw.make),
    modelLine: has(raw.modelLine),
    model: has(raw.model),
    modelCode: has(raw.modelCode),
    unitType: has(raw.unitType || raw.rvType),
    brandSegment: has(raw.brandSegment),
    warrantyStatus: raw.warrantyExpired !== undefined,
    retailSaleDate: has(raw.retailSaleDate),
    unitRegistrationDate: has(raw.warrantyRegistrationDate || raw.unitRegistrationDate),
    warrantyExpirationDate: has(raw.warrantyExpireDate || raw.warrantyExpirationDate),
    warrantyExpirationMileage: has(raw.warrantyExpirationMileage),
    unitStatus: has(raw.status),
  };

  const vin = raw.vin || '';
  const make = raw.make || 'KZ';
  const model = raw.model || '';
  const modelYear = raw.modelYear || '2020';
  const rvType = raw.unitType || raw.rvType || 'TT';

  return {
    vin,
    serialNumber: raw.serialNumber || '',
    secondaryVin: raw.secondaryVin || raw.secondaryVIN || `2${vin.substring(1)}`,
    make,
    modelLine: raw.modelLine || `${model} ${rvType}`,
    model,
    modelCode: raw.modelCode || `${make.substring(0, 2).toUpperCase()}${String(modelYear).slice(-2)}-${model.substring(0, 3).toUpperCase()}`,
    modelYear,
    unitType: raw.unitType || raw.rvType || 'TT',
    brandSegment: raw.brandSegment || `${make} ${rvType === 'TT' ? 'Travel Trailers' : rvType === 'FW' ? 'Fifth Wheels' : 'Motorhomes'}`,
    warrantyStatus: raw.warrantyExpired !== undefined ? (raw.warrantyExpired ? 'Expired' : 'Active') : 'Active',
    retailSaleDate: raw.retailSaleDate || `${modelYear}-06-15T00:00:00`,
    unitRegistrationDate: raw.warrantyRegistrationDate || raw.unitRegistrationDate || `${modelYear}-06-20T00:00:00`,
    warrantyExpirationDate: raw.warrantyExpireDate || raw.warrantyExpirationDate || `${parseInt(modelYear) + 1}-06-15T00:00:00`,
    warrantyExpirationMileage: raw.warrantyExpirationMileage?.toString() || '36000',
    unitStatus: raw.status || 'In Service',
    offlineDate: raw.offlineDate || '',
    floorplan: raw.floorplan,
    warrantyExpireDate: raw.warrantyExpireDate,
    warrantyExpired: raw.warrantyExpired,
    mockCode: raw.mockCode,
    mappedFields,
  };
}

export const useVINs = (
  customerNumber?: string, 
  soldFilter?: boolean,
  page: number = 1,
  pageSize: number = 10,
  sortBy?: string,
  sortDirection?: 'asc' | 'desc'
) => {
  const [vins, setVins] = useState<VINUnit[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const { makeAuthenticatedCall } = useAuthenticatedApi();

  const fetchVINs = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await makeAuthenticatedCall<any>('get-vins', {
          customerNumber,
          page,
          pageSize,
          sortBy,
          sortDirection
        });

        if (error) {
          throw new Error(error);
        }
        
        if (data && data.viNs && Array.isArray(data.viNs)) {
          let filteredVins = data.viNs.map((raw: any) => mapVINFromApi(raw));
          
          if (soldFilter !== undefined) {
            filteredVins = filteredVins.filter((vin: VINUnit) => {
              const hasSoldDate = vin.offlineDate && vin.offlineDate.trim() !== '';
              return soldFilter ? hasSoldDate : !hasSoldDate;
            });
          }
          
          setVins(filteredVins);
          setTotalCount(soldFilter !== undefined ? filteredVins.length : (data.totalCount || filteredVins.length));
        } else if (Array.isArray(data)) {
          setVins(data.map((raw: any) => mapVINFromApi(raw)));
          setTotalCount(data.length);
        } else {
          setVins([]);
          setTotalCount(0);
        }
      } catch (error) {
        console.error('Error fetching VINs:', error);
        setVins([]);
        setTotalCount(0);
      } finally {
        setIsLoading(false);
      }
    };

  useEffect(() => {
    fetchVINs();
  }, [customerNumber, soldFilter, page, pageSize, sortBy, sortDirection, makeAuthenticatedCall]);

  return { vins, isLoading, totalCount, pageSize };
};
